import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SlotBookDialog, SlotInfoDialog, SunfilmDayDialog } from "@/components/SlotDialogs";

const BOOK_ROLES = ["network_admin", "central_sales_team", "studio_owner", "studio_manager"];
const STUDIO_ROLES = ["studio_owner", "studio_manager"];
const DAYS = 30;

const shift = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA");
};
const today = () => new Date().toLocaleDateString("en-CA");
const dow = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" });
const dnum = (iso) => new Date(`${iso}T00:00:00`).getDate();
const mon = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { month: "short" });
const initials = (n) => (n || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export default function SlotChart() {
  const { user } = useAuth();
  const [start, setStart] = useState(today());
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState(null);
  const [info, setInfo] = useState(null);
  const [sfDay, setSfDay] = useState(null);
  const isStudio = STUDIO_ROLES.includes(user.role);
  const canBook = BOOK_ROLES.includes(user.role);

  const load = useCallback(() => {
    api.get("/slot-chart", { params: { start, days: DAYS } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [start]);
  useEffect(load, [load]);

  const { ppfMap, sfMap } = useMemo(() => {
    const p = {}, s = {};
    (data?.bookings || []).forEach((b) => b.dates.forEach((d) => {
      if (b.lane === "sunfilm") (s[`${b.studio_id}|${d}`] ||= []).push(b);
      else p[`${b.studio_id}|${b.slot}|${d}`] = b;
    }));
    return { ppfMap: p, sfMap: s };
  }, [data]);

  const rows = useMemo(() => (data?.studios || []).flatMap((s) => [
    ...Array.from({ length: s.slots }, (_, i) => ({ studio: s, lane: "ppf", slot: i + 1 })),
    { studio: s, lane: "sunfilm" },
  ]), [data]);

  const canRelease = (b) => user.role === "network_admin"
    || (user.role === "central_sales_team" && b.booked_by_id === user.id)
    || (isStudio && b.kind === "studio_block" && b.studio_id === user.studio_id);

  const openBook = (studio, lane, slot, date) => {
    if (!canBook || date < today()) return;
    setBooking({ studio, lane, slot, date });
  };

  return (
    <div data-testid="slot-chart-page">
      <PageHead title="Slot Chart"
        subtitle="Pick a job to place it in a slot. PPF blocks the booking day and the next day; Sunfilm takes 3 jobs a day.">
        <Button variant="outline" data-testid="slot-prev-btn" onClick={() => setStart(shift(start, -DAYS))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" data-testid="slot-today-btn" onClick={() => setStart(today())}>Today</Button>
        <Button variant="outline" data-testid="slot-next-btn" onClick={() => setStart(shift(start, DAYS))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </PageHead>
      <Legend cap={data?.sunfilm_capacity || 3} />
      <Card>
        <CardContent className="overflow-x-auto p-0">
          {!data ? <p className="p-6 text-sm text-muted-foreground">Loading…</p> : (
            <table className="border-separate border-spacing-0 text-xs" data-testid="slot-grid">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[200px] border-b bg-white p-2 text-left font-semibold">Studio</th>
                  {data.dates.map((d) => (
                    <th key={d} className={`min-w-[44px] border-b p-1 text-center font-medium ${d === today() ? "text-crimson-600" : "text-muted-foreground"}`}>
                      <div>{dow(d)}</div><div className="text-sm text-brand-ink">{dnum(d)}</div><div>{mon(d)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.studio.id}-${r.lane}-${r.slot || 0}`}
                    data-testid={r.lane === "ppf" ? `slot-row-${r.studio.id}-${r.slot}` : `slot-row-${r.studio.id}-sunfilm`}>
                    <td className={`sticky left-0 z-10 border-b bg-white p-2 ${r.lane === "ppf" ? "font-medium text-brand-ink" : "text-muted-foreground"}`}>
                      {r.lane === "ppf"
                        ? <>{r.studio.name}<span className="ml-1 text-muted-foreground">· PPF{r.studio.slots > 1 ? ` ${r.slot}` : ""}</span></>
                        : <span className="pl-3">↳ Sunfilm</span>}
                    </td>
                    {data.dates.map((d) => r.lane === "ppf"
                      ? <PpfCell key={d} r={r} d={d} b={ppfMap[`${r.studio.id}|${r.slot}|${d}`]} canBook={canBook}
                          onOpen={(b) => (b ? setInfo(b) : openBook(r.studio, "ppf", r.slot, d))} />
                      : <SfCell key={d} r={r} d={d} list={sfMap[`${r.studio.id}|${d}`] || []} cap={data.sunfilm_capacity}
                          onOpen={() => setSfDay({ studio: r.studio, date: d })} />)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <SlotBookDialog target={booking} user={user} onClose={() => setBooking(null)}
        onSaved={() => { setBooking(null); setSfDay(null); load(); }} />
      <SlotInfoDialog booking={info} canRelease={info && canRelease(info)} onClose={() => setInfo(null)}
        onReleased={() => { setInfo(null); setSfDay(null); load(); }} />
      <SunfilmDayDialog day={sfDay} cap={data?.sunfilm_capacity || 3}
        list={sfDay ? sfMap[`${sfDay.studio.id}|${sfDay.date}`] || [] : []}
        canAdd={sfDay && canBook && sfDay.date >= today()}
        onAdd={() => openBook(sfDay.studio, "sunfilm", null, sfDay.date)}
        onPick={(b) => { setSfDay(null); setInfo(b); }} onClose={() => setSfDay(null)} />
    </div>
  );
}

const PpfCell = ({ r, d, b, canBook, onOpen }) => {
  const past = d < today();
  const first = b && b.start_date === d;
  const cls = b ? (b.kind === "studio_block" ? "bg-amber-500 text-white" : "bg-red-500 text-white")
    : past ? "bg-slate-100" : "bg-emerald-100 hover:bg-emerald-300";
  return (
    <td className={`border-b border-r border-white p-0.5 ${first ? "border-l-2 border-l-brand-ink/60" : ""}`}>
      <button type="button" data-testid={`slot-cell-${r.studio.id}-${r.slot}-${d}`}
        title={b ? `${b.customer_name || b.note || "Blocked"} · by ${b.booked_by_name}` : past ? "" : "Open"}
        onClick={() => (b || (!past && canBook)) && onOpen(b)}
        className={`h-9 w-full rounded transition-colors duration-150 ${cls} ${!b && (past || !canBook) ? "cursor-default" : ""}`}>
        {first ? <span className="block truncate px-0.5 text-[10px] font-semibold">{initials(b.customer_name) || "Blk"}</span> : null}
      </button>
    </td>
  );
};

const SfCell = ({ r, d, list, cap, onOpen }) => {
  const past = d < today();
  const n = list.length;
  const cls = n >= cap ? "bg-red-500 text-white" : n > 0 ? "bg-orange-200 text-orange-900 hover:bg-orange-300"
    : past ? "bg-slate-100" : "bg-sky-100 ring-1 ring-inset ring-sky-200 hover:bg-sky-200";
  return (
    <td className="border-b border-r border-white p-0.5">
      <button type="button" data-testid={`slot-sf-cell-${r.studio.id}-${d}`}
        onClick={() => (n || !past) && onOpen()}
        className={`h-7 w-full rounded text-[10px] font-semibold transition-colors duration-150 ${cls}`}>
        {n ? `${n}/${cap}` : ""}
      </button>
    </td>
  );
};

const Legend = ({ cap }) => (
  <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground" data-testid="slot-legend">
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-200" />PPF open</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-500" />Booked / full</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-500" />Blocked by studio</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-sky-100 ring-1 ring-sky-200" />Sunfilm open</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-orange-200" />Sunfilm partly booked (of {cap})</span>
    <span className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" />PPF = 2 days · Sunfilm = 1 day</span>
  </div>
);
